import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { CronBuilder } from './CronBuilder';
import { SchedulePreview } from './SchedulePreview';
import { ScheduleConfig } from '../types/pipeline';
import { Loader2, Calendar, Settings, Bell } from 'lucide-react';
import { toast } from 'sonner';

interface ScheduleEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipelineId: string;
  pipelineName: string;
  currentSchedule?: ScheduleConfig;
  onSave: (config: ScheduleConfig) => Promise<void>;
  onDelete?: () => Promise<void>;
}

export function ScheduleEditorDialog({
  open,
  onOpenChange,
  pipelineId: _pipelineId,
  pipelineName,
  currentSchedule,
  onSave,
  onDelete,
}: ScheduleEditorDialogProps) {
  void _pipelineId;
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Schedule state
  const [enabled, setEnabled] = useState(currentSchedule?.enabled ?? true);
  const [cronExpression, setCronExpression] = useState(
    currentSchedule?.expression || '0 3 * * 1'
  );
  const [timezone, setTimezone] = useState(
    currentSchedule?.timezone || 'Asia/Makassar'
  );
  const [description, setDescription] = useState(
    currentSchedule?.description || ''
  );

  // Retry policy state
  const [maxRetries, setMaxRetries] = useState(
    currentSchedule?.retryPolicy?.maxRetries ?? 3
  );
  const [retryDelayMinutes, setRetryDelayMinutes] = useState(
    currentSchedule?.retryPolicy?.retryDelayMinutes ?? 15
  );

  // Notification state
  const [notifyOnFailure, setNotifyOnFailure] = useState(
    currentSchedule?.notifyOnFailure ?? true
  );

  // Reset form when dialog opens/closes or currentSchedule changes
  useEffect(() => {
    if (open && currentSchedule) {
      setEnabled(currentSchedule.enabled);
      setCronExpression(currentSchedule.expression);
      setTimezone(currentSchedule.timezone);
      setDescription(currentSchedule.description || '');
      setMaxRetries(currentSchedule.retryPolicy?.maxRetries ?? 3);
      setRetryDelayMinutes(currentSchedule.retryPolicy?.retryDelayMinutes ?? 15);
      setNotifyOnFailure(currentSchedule.notifyOnFailure ?? true);
    } else if (open && !currentSchedule) {
      // Set defaults for new schedule
      setEnabled(true);
      setCronExpression('0 3 * * 1');
      setTimezone('Asia/Makassar');
      setDescription('');
      setMaxRetries(3);
      setRetryDelayMinutes(15);
      setNotifyOnFailure(true);
    }
  }, [open, currentSchedule]);

  const handleSave = async () => {
    // Validation
    if (!cronExpression || cronExpression.split(' ').length !== 5) {
      toast.error('Invalid cron expression');
      return;
    }

    if (maxRetries < 0 || maxRetries > 10) {
      toast.error('Max retries must be between 0 and 10');
      return;
    }

    if (retryDelayMinutes < 1 || retryDelayMinutes > 1440) {
      toast.error('Retry delay must be between 1 and 1440 minutes');
      return;
    }

    setSaving(true);
    try {
      const config: ScheduleConfig = {
        type: 'cron',
        expression: cronExpression,
        timezone,
        enabled,
        description: description.trim() || undefined,
        notifyOnFailure,
        retryPolicy: {
          maxRetries,
          retryDelayMinutes,
        },
      };

      await onSave(config);
      toast.success(
        enabled
          ? 'Pipeline schedule configured successfully'
          : 'Pipeline schedule disabled'
      );
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast.error('Failed to save schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    if (!confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    setDeleting(true);
    try {
      await onDelete();
      toast.success('Pipeline schedule deleted');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      toast.error('Failed to delete schedule. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule Pipeline</DialogTitle>
          <DialogDescription>
            Configure automated execution schedule for <strong>{pipelineName}</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/50">
          <div className="space-y-0.5">
            <Label className="text-base">Enable Schedule</Label>
            <p className="text-sm text-muted-foreground">
              {enabled
                ? 'Pipeline will run automatically on schedule'
                : 'Pipeline will not run automatically'}
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <Separator />

        <Tabs defaultValue="schedule" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="schedule">
              <Calendar className="h-4 w-4 mr-2" />
              Schedule
            </TabsTrigger>
            <TabsTrigger value="retry">
              <Settings className="h-4 w-4 mr-2" />
              Retry Policy
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
          </TabsList>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                placeholder="e.g., Weekly data refresh from BPS APIs"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Configure Schedule</h3>
                <CronBuilder
                  value={cronExpression}
                  timezone={timezone}
                  onChange={setCronExpression}
                  onTimezoneChange={setTimezone}
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium">Preview</h3>
                <SchedulePreview
                  cronExpression={cronExpression}
                  timezone={timezone}
                  count={5}
                />
              </div>
            </div>
          </TabsContent>

          {/* Retry Policy Tab */}
          <TabsContent value="retry" className="space-y-4 mt-4">
            <div className="rounded-lg border p-4 bg-muted/50">
              <p className="text-sm text-muted-foreground">
                Configure how the system should handle failed pipeline executions.
                Retries help handle transient failures like temporary network issues.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Maximum Retries</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={maxRetries}
                  onChange={(e) => setMaxRetries(parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Number of retry attempts (0-10). Set to 0 to disable retries.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Retry Delay (Minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  max="1440"
                  value={retryDelayMinutes}
                  onChange={(e) =>
                    setRetryDelayMinutes(parseInt(e.target.value) || 1)
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Wait time between retry attempts (1-1440 minutes).
                </p>
              </div>

              {maxRetries > 0 && (
                <div className="rounded-lg border p-4">
                  <h4 className="text-sm font-medium mb-2">Retry Timeline</h4>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>• Attempt 1: Immediate</p>
                    {Array.from({ length: maxRetries }, (_, i) => (
                      <p key={i}>
                        • Attempt {i + 2}: After {retryDelayMinutes * (i + 1)}{' '}
                        minutes
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-4 mt-4">
            <div className="rounded-lg border p-4 bg-muted/50">
              <p className="text-sm text-muted-foreground">
                Configure how you want to be notified about pipeline execution
                status.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Notify on Failure</Label>
                  <p className="text-sm text-muted-foreground">
                    Send notification when pipeline execution fails (after all retries)
                  </p>
                </div>
                <Switch
                  checked={notifyOnFailure}
                  onCheckedChange={setNotifyOnFailure}
                />
              </div>

              <div className="rounded-lg border p-4">
                <h4 className="text-sm font-medium mb-2">
                  Notification Channels (Coming Soon)
                </h4>
                <p className="text-sm text-muted-foreground">
                  Email, Slack, and webhook notifications will be available in a
                  future update.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-between">
          <div>
            {currentSchedule && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting || saving}
              >
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete Schedule
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving || deleting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving || deleting}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {enabled ? 'Save Schedule' : 'Disable Schedule'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
